import { useLocalStorage } from "@rehooks/local-storage";
import availableComponents from "virtual:ds-components";

import { Cross } from "components/icons/generated";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import ComboBoxInput from "ds/components/ComboBox/Input";
import Tag from "ds/components/Tag";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";

import { BANNER_KEY, EXCLUDE_KEY, FILTER_KEY, HIGHLIGHTS_ENABLED } from "./constants";
import { useComponentsOnPage } from "./useComponentsOnPage";

const XRay = () => {
  const [isEnabled, setIsEnabled] = useLocalStorage(HIGHLIGHTS_ENABLED, false);
  const [isExcludeSelected, setIsExcludeSelected] = useLocalStorage(EXCLUDE_KEY, false);
  const [filters, setFilters] = useLocalStorage<string[]>(FILTER_KEY, []);

  const appendFilter = (option: string | null) => {
    if (option) {
      setFilters([...(filters || []), option]);
    }
  };

  const removeFilter = (filter: string) => {
    setFilters(filters.filter((f) => f !== filter));
  };

  const nonSelectedComponents = availableComponents.filter(
    (component) => !filters.includes(component)
  );

  const componentsOnPage = useComponentsOnPage();

  const nonSelectedComponentsOptions = nonSelectedComponents
    .sort((a, b) => (componentsOnPage.get(b) ?? 0) - (componentsOnPage.get(a) ?? 0))
    .map((component) => ({
      label: component,
      value: component,
      postfix: componentsOnPage.get(component)
        ? `${componentsOnPage.get(component) ?? 0} on page`
        : "not on page",
    }));

  return (
    <Box direction="column" gap="large" align="start">
      <Banner
        variant="info"
        title="Highlight Design System Components"
        storageKey={BANNER_KEY}
        fullWidth
      >
        This will highlight components that have displayName matching the filter pattern.
      </Banner>

      <Box direction="column" gap="medium" align="start" fullWidth>
        <Toggle
          variant="switch"
          onChange={setIsEnabled}
          isSelected={isEnabled}
          id="highlight-ds-components"
        >
          <Typography variant="p-body3" tag="span">
            Highlight Components
          </Typography>
        </Toggle>
        <Toggle
          variant="switch"
          onChange={setIsExcludeSelected}
          isSelected={isExcludeSelected}
          id="exclude-ds-components"
        >
          <Typography variant="p-body3" tag="span">
            Toggle filtering mode
          </Typography>
        </Toggle>
        <ComboBox
          value={null}
          label={`Choose components to ${isExcludeSelected ? "exclude" : "include"}`}
          items={nonSelectedComponentsOptions}
          onChange={appendFilter}
          renderInput={(inputProps) => (
            <Box direction="row" gap="small" align="center" fullWidth>
              <ComboBoxInput {...inputProps} />
              {filters.length > 0 && (
                <Button startIcon={Cross} onPress={() => setFilters([])} variant="secondary">
                  Clear filters
                </Button>
              )}
            </Box>
          )}
          placeholder="Select or type component name"
          helperText={filters.length === 0 ? "By default highlights all" : undefined}
        >
          {(item) => <ComboBoxDefaultItem id={item.value} label={item.label} />}
        </ComboBox>
        <Box direction="row" gap="small" wrap>
          {filters.map((tag) => (
            <Tag key={tag} tag={tag} onRemove={() => removeFilter(tag)} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default XRay;
